import React from 'react';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { Size, Variation } from '@gokrew/models/ui.types';
import type { ButtonProps, IconButtonProps } from '@gokrew/models/button';

/**
 * Minimum CSS module contract each app must satisfy.
 * Apps may add extra keys for app-specific variants.
 */
export interface ButtonStyles {
  btnBase: string;
  btnSm: string;
  btnMd: string;
  btnLg: string;
  btnDisabled: string;
  iconButton?: string;
  iconBtnDisabled?: string;
  [key: string]: string | undefined;
}

const ICON_SIZES: Record<Size, number> = { xs: 12, sm: 14, md: 16, lg: 20 };

/**
 * Derives the CSS module key for a given variant name.
 * e.g. 'primary' → 'btnPrimary', 'solidPrimary' → 'btnSolidPrimary'
 */
function variantKey(variant: Variation): string {
  return `btn${variant.charAt(0).toUpperCase()}${variant.slice(1)}`;
}

/**
 * Factory that creates Button and IconButton components bound to a specific
 * CSS module. Each app passes its own styles object so branding is fully
 * independent while the button logic stays centralised.
 *
 * Usage:
 *   import styles from './Button.module.css';
 *   import { createButton } from '@gokrew/ui';
 *   export const { Button, IconButton } = createButton(styles);
 */
export function createButton(styles: ButtonStyles) {
  function Button({
    variant = 'primary',
    size = 'md',
    disabled = false,
    children,
    className = '',
    IconSuffix = null,
    IconPrefix = null,
    onClick,
    type = 'button',
    ...props
  }: ButtonProps) {
    const variantClass = styles[variantKey(variant)] ?? '';
    const sizeClass =
      styles[`btn${size.toUpperCase()[0]}${size.slice(1)}`] ?? '';

    return (
      <button
        type={type}
        disabled={disabled}
        onClick={!disabled ? onClick : undefined}
        className={cn(
          styles.btnBase,
          sizeClass,
          disabled ? styles.btnDisabled : variantClass,
          className
        )}
        {...props}
      >
        {IconPrefix && <IconPrefix size={ICON_SIZES[size]} />}
        {children}
        {IconSuffix && <IconSuffix size={ICON_SIZES[size]} />}
      </button>
    );
  }

  function IconButton({
    Icon = null,
    size = 'md',
    className = '',
    disabled = false,
    onClick,
    ...props
  }: React.ComponentProps<LucideIcon> & IconButtonProps) {
    if (!Icon) return null;

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={!disabled ? onClick : undefined}
        className={cn(
          styles.iconButton,
          disabled && styles.iconBtnDisabled,
          className
        )}
      >
        <Icon {...props} size={ICON_SIZES[size]} />
      </button>
    );
  }

  return { Button, IconButton };
}
